import React, { useState } from "react";
import { jsPDF } from "jspdf";
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Box,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";

export default function CARPDF({ card }) {
  const [open, setOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfFileName, setPdfFileName] = useState("");

  const handleClose = () => {
    setOpen(false);
    // Clean up the URL object when modal is closed
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl("");
    }
  };

  const handleDownload = () => {
    // Download the PDF file
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = pdfFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const generatePDF = () => {
    try {
      // Initialize PDF document
      const doc = new jsPDF();
      doc.setFontSize(10);

      // Helper functions
      const isTrue = (field) => {
        return card && card[field] === "true";
      };

      const formatDate = (dateString) => {
        if (!dateString) return "";
        return dateString;
      };

      const getFieldValue = (value, fallback = "") => {
        if (value === undefined || value === null || value === "")
          return fallback;
        if (value === "false" || value === "true") {
          return value === "true" ? "Yes" : "No";
        }
        if (typeof value === "object" && value !== null) {
          return (
            value.zc_display_value ||
            value.Name ||
            value.account_name ||
            value.SO_name ||
            fallback
          );
        }
        return value || fallback;
      };

      // Function to draw a bordered cell with text
      const drawCell = (
        x,
        y,
        width,
        height,
        text = "",
        fillColor = null,
        bold = false
      ) => {
        // Fill background if provided
        if (fillColor) {
          doc.setFillColor(...fillColor);
          doc.rect(x, y, width, height, "F");
        }

        // Draw border
        doc.setDrawColor(0);
        doc.rect(x, y, width, height, "S");

        // Add text if provided
        if (text) {
          // Save current font style
          const currentFontStyle = doc.getFont().fontStyle;

          // Set font style based on bold parameter
          doc.setFont("helvetica", bold ? "bold" : "normal");

          const fontSize = doc.getFontSize();
          const textWidth =
            (doc.getStringUnitWidth(text) * fontSize) /
            doc.internal.scaleFactor;

          // If text is too wide, split it into multiple lines
          if (textWidth > width - 6) {
            const splitText = doc.splitTextToSize(text, width - 6);
            const lineHeight = fontSize / doc.internal.scaleFactor;
            const textHeight = splitText.length * lineHeight;
            const textY = y + (height - textHeight) / 2 + lineHeight;

            doc.text(splitText, x + 3, textY);
          } else {
            // Center text vertically
            doc.text(text, x + 3, y + height / 2 + fontSize / 3);
          }

          // Restore font style
          doc.setFont("helvetica", currentFontStyle);
        }
      };

      // Function to draw a checkbox
      const drawCheckbox = (x, y, text, isChecked, labelOffset = 10) => {
        // Draw the checkbox
        doc.setDrawColor(0);
        doc.rect(x, y - 4, 5, 5, "S");

        // Fill the checkbox if checked
        if (isChecked) {
          doc.setFillColor(0);
          doc.rect(x + 1, y - 3, 3, 3, "F");
        }

        // Add label text
        doc.text(text, x + labelOffset, y);
      };

      // Draw the CAR form
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 10;
      const formWidth = pageWidth - 2 * margin;

      // Title bar
      let y = margin;
      drawCell(margin, y, formWidth, 20, "", null, true);
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text(
        `Corrective & preventive action request`,
        pageWidth / 2,
        y + 12,
        { align: "center" }
      );
      doc.setFontSize(10);
      doc.text(`(CAR / PAR) - Rev 0`, pageWidth / 2, y + 18, {
        align: "center",
      });
      doc.setFontSize(10);

      // CAR Number box
      drawCell(formWidth - 40 + margin, y, 40, 20, "", null, false);
      doc.setFont("helvetica", "bold");
      doc.text("CAR / PAR #", formWidth - 20 + margin, y + 8, {
        align: "center",
      });
      doc.setFont("helvetica", "normal");
      doc.text(
        getFieldValue(card?.carNumber || card?.CAR_number, ""),
        formWidth - 20 + margin,
        y + 16,
        { align: "center" }
      );

      // Type section
      y += 20;
      const typeRowHeight = 32;
      drawCell(margin, y, 50, typeRowHeight, "Type:\nCheck One", null, true);
      drawCell(margin + 50, y, 140, typeRowHeight, "", null, false);

      // Get type checkboxes
      const isCorrectiveAction = isTrue("Corrective_action");
      const isPreventiveAction = isTrue("Preventive_action_potential_issue");
      const isOpportunity = isTrue("Opportunity_for_Improvement_suggestion");

      // Draw checkboxes for type section
      doc.setFont("helvetica", "normal");
      drawCheckbox(
        margin + 55,
        y + 10,
        "Corrective action (existing issue)",
        isCorrectiveAction
      );

      drawCheckbox(
        margin + 55,
        y + 22,
        "Opportunity for Improvement /\nsuggestion",
        isOpportunity
      );

      drawCell(margin + 190, y, 140, typeRowHeight, "", null, false);
      drawCheckbox(
        margin + 195,
        y + 10,
        "Preventive action\n(potential issue)",
        isPreventiveAction
      );

      // Name & Date
      drawCell(
        margin + 50 + 140 + 140,
        y,
        formWidth - 50 - 140 - 140,
        typeRowHeight,
        "",
        null,
        false
      );

      // Format the Name and Date fields with title and value on separate lines
      doc.setFont("helvetica", "bold");
      doc.text("Name:", margin + 50 + 140 + 145, y + 10);
      doc.setFont("helvetica", "normal");
      doc.text(
        getFieldValue(card?.CAR_Reported_By, ""),
        margin + 50 + 140 + 145,
        y + 16
      );

      doc.setFont("helvetica", "bold");
      doc.text("Date:", margin + 50 + 140 + 145, y + 22);
      doc.setFont("helvetica", "normal");
      doc.text(
        formatDate(card?.date || card?.CAR_Date),
        margin + 50 + 140 + 145,
        y + 28
      );

      // Source section
      y += typeRowHeight;
      const sourceRowHeight = 40;
      drawCell(
        margin,
        y,
        50,
        sourceRowHeight,
        "Source:\nCheck One",
        null,
        true
      );

      // First column of source checkboxes
      drawCell(margin + 50, y, 110, sourceRowHeight, "", null, false);
      drawCheckbox(
        margin + 55,
        y + 10,
        "Employee Feedback",
        isTrue("Employee_Source_Creator")
      );

      drawCheckbox(
        margin + 55,
        y + 20,
        "External audit finding",
        isTrue("ExternalAuditFindingCreator")
      );

      drawCheckbox(margin + 55, y + 30, "Other", isTrue("OtherSourceCreator"));

      // Second column of source checkboxes
      drawCell(margin + 50 + 110, y, 110, sourceRowHeight, "", null, false);
      drawCheckbox(
        margin + 50 + 110 + 5,
        y + 10,
        "Customer feedback",
        isTrue("CustomerSourceCreator")
      );

      drawCheckbox(
        margin + 50 + 110 + 5,
        y + 30,
        "Internal audit finding",
        isTrue("InternalAuditFindingCreator")
      );

      // Third column of source checkboxes
      drawCell(
        margin + 50 + 110 + 110,
        y,
        formWidth - 50 - 110 - 110,
        sourceRowHeight,
        "",
        null,
        false
      );
      drawCheckbox(
        margin + 50 + 110 + 110 + 5,
        y + 10,
        "Supplier /\nsubcontractor\nfeedback",
        isTrue("SupplierSourceCreator")
      );

      drawCheckbox(
        margin + 50 + 110 + 110 + 5,
        y + 30,
        "Management\nreview action item",
        isTrue("ManagementReviewCreator")
      );

      // Process section
      y += sourceRowHeight;
      const processRowHeight = 40;
      drawCell(
        margin,
        y,
        50,
        processRowHeight,
        "Process:\nCheck One",
        null,
        true
      );

      // First column of process checkboxes
      drawCell(margin + 50, y, 110, processRowHeight, "", null, false);
      drawCheckbox(
        margin + 55,
        y + 10,
        "Estimating, Scheduling, Art",
        isTrue("EstimatingSchedulingArtCreator")
      );

      drawCheckbox(
        margin + 55,
        y + 30,
        "Installation",
        isTrue("InstallationSourceCreator")
      );

      // Second column of process checkboxes
      drawCell(margin + 50 + 110, y, 130, processRowHeight, "", null, false);
      drawCheckbox(
        margin + 50 + 110 + 5,
        y + 10,
        "Purchasing, Receiving, Shipping",
        isTrue("PurchasingReceivingShippingCreator")
      );

      drawCheckbox(
        margin + 50 + 110 + 5,
        y + 30,
        "Other",
        isTrue("OtherprocessCreator")
      );

      // Third column of process checkboxes
      drawCell(
        margin + 50 + 110 + 130,
        y,
        formWidth - 50 - 110 - 130,
        processRowHeight,
        "",
        null,
        false
      );
      drawCheckbox(
        margin + 50 + 110 + 130 + 5,
        y + 20,
        "Printing &\nfinishing",
        isTrue("PrintingFinishingCreator")
      );

      // Priority section
      y += processRowHeight;
      const priorityRowHeight = 20;
      drawCell(
        margin,
        y,
        50,
        priorityRowHeight,
        "Priority:\nCheck One",
        null,
        true
      );

      // Priority checkboxes
      drawCell(
        margin + 50,
        y,
        formWidth - 50,
        priorityRowHeight,
        "",
        null,
        false
      );

      drawCheckbox(margin + 55, y + 12, "Low", isTrue("LowPriorityCreator"));

      drawCheckbox(
        margin + 130,
        y + 12,
        "Medium",
        isTrue("MediumPriorityCreator")
      );

      drawCheckbox(margin + 220, y + 12, "High", isTrue("HighPriorityCreator"));

      drawCheckbox(
        margin + 280,
        y + 12,
        "Urgent / Critical",
        isTrue("UrgentCriticalCreator")
      );

      // Problem description
      y += priorityRowHeight;
      const descriptionHeaderHeight = 20;
      drawCell(
        margin,
        y,
        formWidth,
        descriptionHeaderHeight,
        "Describe the issue or problem in detail:",
        null,
        true
      );

      // Description content area
      const descriptionHeight = 60;
      y += descriptionHeaderHeight;
      drawCell(margin, y, formWidth, descriptionHeight, "", null, false);

      // Add problem description text
      const problemDescText = getFieldValue(
        card?.Describe_the_issue_or_problem_in_detail,
        ""
      );
      const splitProblemDesc = doc.splitTextToSize(
        problemDescText,
        formWidth - 10
      );
      doc.text(splitProblemDesc, margin + 5, y + 10);

      // Stop here line
      y += descriptionHeight;
      drawCell(margin, y, formWidth, 20, "", [173, 216, 230], true);
      doc.setFont("helvetica", "bold");
      doc.text(
        "Stop here. Submit CAR / PAR to Mark Woster, Manager",
        pageWidth / 2,
        y + 12,
        { align: "center" }
      );

      // Assignment info
      y += 20;
      const assignmentHeight = 20;

      // Assignment row with 3 columns
      drawCell(margin, y, formWidth / 3, assignmentHeight, "", null, false);
      doc.setFont("helvetica", "bold");
      doc.text("CAR / PAR Assigned to:", margin + 5, y + 8);
      doc.setFont("helvetica", "normal");
      doc.text(
        getFieldValue(card?.CAR_PAR_Assigned_to || card?.assignedTo, ""),
        margin + 5,
        y + 16
      );

      drawCell(
        margin + formWidth / 3,
        y,
        formWidth / 3,
        assignmentHeight,
        "",
        null,
        false
      );
      doc.setFont("helvetica", "bold");
      doc.text("Date Assigned:", margin + formWidth / 3 + 5, y + 8);
      doc.setFont("helvetica", "normal");
      doc.text(
        formatDate(card?.Date_Assigned),
        margin + formWidth / 3 + 5,
        y + 16
      );

      drawCell(
        margin + (2 * formWidth) / 3,
        y,
        formWidth / 3,
        assignmentHeight,
        "",
        null,
        false
      );
      doc.setFont("helvetica", "bold");
      doc.text("Respond by:", margin + (2 * formWidth) / 3 + 5, y + 8);
      doc.setFont("helvetica", "normal");
      doc.text(
        formatDate(card?.Respond_by || card?.respondBy),
        margin + (2 * formWidth) / 3 + 5,
        y + 16
      );

      // To be completed by assignee
      y += assignmentHeight;
      drawCell(margin, y, formWidth, 20, "", [173, 216, 230], true);
      doc.setFont("helvetica", "bold");
      doc.text("To be completed by the Assignee", pageWidth / 2, y + 12, {
        align: "center",
      });

      // Containment section
      y += 20;
      const containmentHeaderHeight = 20;
      drawCell(margin, y, formWidth, containmentHeaderHeight, "", null, false);
      doc.setFont("helvetica", "bold");
      doc.text("Containment / Immediate Corrections:", margin + 5, y + 12);
      doc.setFont("helvetica", "normal");
      doc.text(
        "(Required only for corrective action requests.)",
        margin + 170,
        y + 12
      );

      // Containment content
      const containmentHeight = 40;
      y += containmentHeaderHeight;
      drawCell(margin, y, formWidth, containmentHeight, "", null, false);

      // Add containment text
      const containmentText = getFieldValue(
        card?.Containment_Immediate_Corrections,
        ""
      );
      const splitContainmentText = doc.splitTextToSize(
        containmentText,
        formWidth - 10
      );
      doc.text(splitContainmentText, margin + 5, y + 10);

      // Root cause
      y += containmentHeight;
      const rootCauseHeaderHeight = 20;
      drawCell(margin, y, formWidth, rootCauseHeaderHeight, "", null, false);
      doc.setFont("helvetica", "bold");
      doc.text("Root Cause of Problem:", margin + 5, y + 12);
      doc.setFont("helvetica", "normal");
      doc.text(
        "(Required for all corrective and preventive issues; optional for suggestions for improvement.)",
        margin + 110,
        y + 12
      );

      // Root cause content
      const rootCauseHeight = 40;
      y += rootCauseHeaderHeight;
      drawCell(margin, y, formWidth, rootCauseHeight, "", null, false);

      // Add root cause text
      const rootCauseText = getFieldValue(card?.Root_cause_of_Problem, "");
      const splitRootCauseText = doc.splitTextToSize(
        rootCauseText,
        formWidth - 10
      );
      doc.text(splitRootCauseText, margin + 5, y + 10);

      // Check if we need a new page
      if (y + rootCauseHeight + 100 > pageHeight) {
        doc.addPage();
        y = margin;
      } else {
        y += rootCauseHeight;
      }

      // Action plan
      const actionPlanHeaderHeight = 20;
      drawCell(margin, y, formWidth, actionPlanHeaderHeight, "", null, false);
      doc.setFont("helvetica", "bold");
      doc.text("Action Plan:", margin + 5, y + 12);
      doc.setFont("helvetica", "normal");
      doc.text(
        "(Actions taken to eliminate root cause, resolve issue and prevent recurrence.)",
        margin + 70,
        y + 12
      );

      // Action plan content
      const actionPlanHeight = 40;
      y += actionPlanHeaderHeight;
      drawCell(margin, y, formWidth, actionPlanHeight, "", null, false);

      // Add action plan text
      const actionPlanText = getFieldValue(card?.Action_Plan, "");
      const splitActionPlanText = doc.splitTextToSize(
        actionPlanText,
        formWidth - 10
      );
      doc.text(splitActionPlanText, margin + 5, y + 10);

      // Test section seen in screenshots
      y += actionPlanHeight;
      const testingHeight = 70;
      drawCell(margin, y, formWidth, testingHeight, "", null, false);

      doc.setFont("helvetica", "bold");
      doc.text("Testing:", margin + 5, y + 10);
      doc.setFont("helvetica", "normal");
      doc.text(
        "It allows developers and testers to validate the functionality of software applications without",
        margin + 5,
        y + 20
      );
      doc.text(
        "using real data that could be sensitive or have privacy implications.",
        margin + 5,
        y + 30
      );
      doc.setFont("helvetica", "bold");
      doc.text("Development:", margin + 5, y + 45);
      doc.setFont("helvetica", "normal");
      doc.text(
        "Dummy data helps in building and refining applications by providing a consistent and predictable",
        margin + 5,
        y + 55
      );
      doc.text("dataset to work with.", margin + 5, y + 65);

      // Date completed and name
      y += testingHeight;
      const completionHeight = 20;
      drawCell(margin, y, formWidth / 2, completionHeight, "", null, false);
      doc.setFont("helvetica", "bold");
      doc.text("Date Action Completed:", margin + 5, y + 8);
      doc.setFont("helvetica", "normal");
      doc.text(formatDate(card?.Date_Action_Completed), margin + 5, y + 16);

      drawCell(
        margin + formWidth / 2,
        y,
        formWidth / 2,
        completionHeight,
        "",
        null,
        false
      );
      doc.setFont("helvetica", "bold");
      doc.text("Name:", margin + formWidth / 2 + 5, y + 8);
      doc.setFont("helvetica", "normal");
      doc.text(
        getFieldValue(card?.Assignee_Name),
        margin + formWidth / 2 + 5,
        y + 16
      );

      // Stop here line 2
      y += completionHeight;
      drawCell(margin, y, formWidth, 20, "", [173, 216, 230], true);
      doc.setFont("helvetica", "bold");
      doc.text(
        "Stop here. Return CAR / PAR to Mark Woster, Manager",
        pageWidth / 2,
        y + 12,
        { align: "center" }
      );

      // Verification section
      y += 20;
      const verificationHeight = 20;
      drawCell(margin, y, formWidth * 0.6, verificationHeight, "", null, false);
      doc.setFont("helvetica", "bold");
      doc.text("CAR / PAR Effectiveness\nverified by:", margin + 5, y + 8);
      doc.setFont("helvetica", "normal");
      doc.text(getFieldValue(card?.Verified_by), margin + 90, y + 14);

      drawCell(
        margin + formWidth * 0.6,
        y,
        formWidth * 0.4,
        verificationHeight,
        "",
        null,
        false
      );
      doc.setFont("helvetica", "bold");
      doc.text("Date:", margin + formWidth * 0.6 + 5, y + 8);
      doc.setFont("helvetica", "normal");
      doc.text(
        formatDate(card?.Verified_Date),
        margin + formWidth * 0.6 + 5,
        y + 16
      );

      // Result section
      y += verificationHeight;
      const resultHeight = 20;
      drawCell(margin, y, formWidth * 0.2, resultHeight, "", null, false);
      doc.setFont("helvetica", "bold");
      doc.text("Result:", margin + 5, y + 12);

      drawCell(
        margin + formWidth * 0.2,
        y,
        formWidth * 0.3,
        resultHeight,
        "",
        null,
        false
      );
      drawCheckbox(
        margin + formWidth * 0.2 + 10,
        y + 12,
        "Pass",
        isTrue("PassCreator")
      );

      drawCell(
        margin + formWidth * 0.5,
        y,
        formWidth * 0.5,
        resultHeight,
        "",
        null,
        false
      );
      drawCheckbox(
        margin + formWidth * 0.5 + 10,
        y + 12,
        "Fail: returned for further processing",
        isTrue("FailCreator")
      );

      // Notes section
      y += resultHeight;
      const notesHeaderHeight = 30;
      drawCell(margin, y, formWidth, notesHeaderHeight, "", null, false);
      doc.setFont("helvetica", "bold");
      doc.text("Notes and\njustification:", margin + 5, y + 10);

      // Notes content
      const notesHeight = 60;
      y += notesHeaderHeight;
      drawCell(margin, y, formWidth, notesHeight, "", null, false);

      // Add notes text
      const notesText = getFieldValue(card?.Notes, "");
      const splitNotesText = doc.splitTextToSize(notesText, formWidth - 10);
      doc.text(splitNotesText, margin + 5, y + 10);

      // Generate PDF blob and create preview URL
      const pdfBlob = doc.output("blob");
      const fileName = `CAR-${card?.carNumber || card?.CAR_number}.pdf`;

      // Create URL for preview
      const url = URL.createObjectURL(pdfBlob);
      setPdfUrl(url);
      setPdfFileName(fileName);
      setOpen(true);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("An error occurred while generating the PDF. Please try again.");
    }
  };

  return (
    <>
      <Tooltip title="Generate PDF">
        <IconButton
          aria-label="generate-pdf"
          sx={{ color: "#4F46E5" }}
          onClick={generatePDF}
        >
          <PictureAsPdfIcon />
        </IconButton>
      </Tooltip>

      {/* PDF Preview Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen={true}
        PaperProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogContent sx={{ flexGrow: 1, overflow: "hidden", padding: 0 }}>
          {pdfUrl && (
            <Box sx={{ width: "100%", height: "100%" }}>
              <iframe
                src={pdfUrl}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
                title="PDF Preview"
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "center",
            padding: 2,
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#4F46E5",
              color: "#FFFFFF",
              mr: 1,
            }}
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
          >
            Download PDF
          </Button>
          <Button
            variant="outlined"
            onClick={handleClose}
            startIcon={<CloseIcon />}
            sx={{ color: "#4F46E5" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
